import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiAlertCircle,
  FiUpload,
  FiFileText,
  FiDownload,
  FiTrash2,
  FiRefreshCw,
  FiPlus,
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import {
  regulatoryComplianceService,
  ComplianceSubmission,
  ComplianceRequirement,
} from '../../services/regulatoryComplianceService';
import { ComplianceSubmissionForm } from './ComplianceSubmissionForm';

interface AustralianCompliancePanelProps {
  organizationId: string;
  onClose?: () => void;
}

export const AustralianCompliancePanel: React.FC<AustralianCompliancePanelProps> = ({
  organizationId,
  onClose,
}) => {
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState<ComplianceSubmission[]>([]);
  const [showSubmissionForm, setShowSubmissionForm] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<ComplianceSubmission | null>(null);
  const [requirements, setRequirements] = useState<ComplianceRequirement[]>([]);

  useEffect(() => {
    loadSubmissions();
    loadRequirements();
  }, [organizationId]);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const data = await regulatoryComplianceService.getStatus(organizationId, {
        country: 'AU',
      });
      setSubmissions(data.submissions || []);
    } catch (error: any) {
      console.error('Failed to load compliance submissions:', error);
      toast.error(error.response?.data?.error || 'Failed to load compliance submissions');
    } finally {
      setLoading(false);
    }
  };

  const loadRequirements = () => {
    const reqs = regulatoryComplianceService.getRequirementsForCountry('AU');
    setRequirements(reqs);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'text-green-400 bg-green-500/20';
      case 'pending':
      case 'in_review':
        return 'text-yellow-400 bg-yellow-500/20';
      case 'rejected':
        return 'text-red-400 bg-red-500/20';
      case 'draft':
        return 'text-gray-400 bg-gray-500/20';
      case 'expired':
        return 'text-orange-400 bg-orange-500/20';
      default:
        return 'text-gray-400 bg-gray-500/20';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <FiCheckCircle className="text-green-400" />;
      case 'rejected':
        return <FiXCircle className="text-red-400" />;
      case 'pending':
      case 'in_review':
        return <FiClock className="text-yellow-400" />;
      default:
        return <FiAlertCircle className="text-gray-400" />;
    }
  };

  const handleSubmissionSuccess = () => {
    setShowSubmissionForm(false);
    loadSubmissions();
    toast.success('Compliance request submitted successfully!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <FiRefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-white">Australian Regulatory Compliance</h2>
            <p className="text-gray-400 mt-1">
              Manage compliance requirements for Australian phone numbers via Telnyx
            </p>
          </div>
          <button
            onClick={() => setShowSubmissionForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
          >
            <FiPlus />
            New Submission
          </button>
        </div>

        {/* Requirements Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          {requirements.map((req, index) => (
            <div
              key={index}
              className="flex items-start gap-3 p-4 bg-slate-900/50 rounded-lg border border-slate-700"
            >
              <div className="mt-0.5">
                {req.required ? (
                  <FiAlertCircle className="text-yellow-400" />
                ) : (
                  <FiFileText className="text-gray-400" />
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-white font-medium">{req.type}</h3>
                <p className="text-sm text-gray-400 mt-1">{req.description}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {req.documentTypes.map((docType) => (
                    <span
                      key={docType}
                      className="text-xs bg-slate-800 text-gray-300 px-2 py-1 rounded"
                    >
                      {docType}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Submissions List */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700">
        <h3 className="text-xl font-semibold text-white mb-4">Compliance Submissions</h3>

        {submissions.length === 0 ? (
          <div className="text-center py-12">
            <FiFileText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 mb-2">No compliance submissions yet</p>
            <p className="text-sm text-gray-500">
              Submit a new compliance request to activate Australian phone numbers
            </p>
            <button
              onClick={() => setShowSubmissionForm(true)}
              className="mt-4 px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
            >
              Create First Submission
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {submissions.map((submission) => (
              <motion.div
                key={submission.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-slate-900/50 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors cursor-pointer"
                onClick={() => setSelectedSubmission(submission)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(submission.status)}
                        <span
                          className={`text-sm font-medium px-3 py-1 rounded-full ${getStatusColor(
                            submission.status
                          )}`}
                        >
                          {submission.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      {submission.requirementGroupId && (
                        <span className="text-xs text-gray-500">
                          Group: {submission.requirementGroupId.slice(0, 8)}...
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-3">
                      <div>
                        <p className="text-xs text-gray-500">Phone Numbers</p>
                        <p className="text-sm text-white mt-1">
                          {submission.phoneNumbers?.length || 0} number(s)
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Documents</p>
                        <p className="text-sm text-white mt-1">
                          {submission.documents?.length || 0} uploaded
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Submitted</p>
                        <p className="text-sm text-white mt-1">
                          {submission.submittedAt
                            ? new Date(submission.submittedAt).toLocaleDateString()
                            : 'Draft'}
                        </p>
                      </div>
                      {submission.expiresAt && (
                        <div>
                          <p className="text-xs text-gray-500">Expires</p>
                          <p className="text-sm text-white mt-1">
                            {new Date(submission.expiresAt).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                    </div>

                    {submission.rejectionReason && (
                      <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <p className="text-xs text-red-400 font-medium mb-1">Rejection Reason:</p>
                        <p className="text-sm text-red-300">{submission.rejectionReason}</p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Submission Form Modal */}
      <AnimatePresence>
        {showSubmissionForm && (
          <ComplianceSubmissionForm
            organizationId={organizationId}
            onClose={() => setShowSubmissionForm(false)}
            onSuccess={handleSubmissionSuccess}
          />
        )}
      </AnimatePresence>

      {/* Submission Details Modal */}
      <AnimatePresence>
        {selectedSubmission && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedSubmission(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-slate-900 rounded-2xl border border-slate-700 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-white">Submission Details</h3>
                  <button
                    onClick={() => setSelectedSubmission(null)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <FiXCircle className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Status */}
                <div>
                  <p className="text-sm text-gray-400 mb-2">Status</p>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(selectedSubmission.status)}
                    <span
                      className={`text-sm font-medium px-3 py-1 rounded-full ${getStatusColor(
                        selectedSubmission.status
                      )}`}
                    >
                      {selectedSubmission.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Phone Numbers */}
                <div>
                  <p className="text-sm text-gray-400 mb-2">Phone Numbers</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedSubmission.phoneNumbers?.map((number) => (
                      <span
                        key={number}
                        className="px-3 py-1 bg-slate-800 text-white rounded-lg text-sm"
                      >
                        {number}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Documents */}
                <div>
                  <p className="text-sm text-gray-400 mb-2">Uploaded Documents</p>
                  <div className="space-y-2">
                    {selectedSubmission.documents?.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between p-3 bg-slate-800 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <FiFileText className="text-blue-400" />
                          <div>
                            <p className="text-sm text-white">{doc.fileName}</p>
                            <p className="text-xs text-gray-400">{doc.type}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-xs px-2 py-1 rounded ${
                              doc.status === 'approved'
                                ? 'bg-green-500/20 text-green-400'
                                : doc.status === 'rejected'
                                ? 'bg-red-500/20 text-red-400'
                                : 'bg-yellow-500/20 text-yellow-400'
                            }`}
                          >
                            {doc.status}
                          </span>
                          <a
                            href={doc.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-400 hover:text-blue-300"
                          >
                            <FiDownload />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Metadata */}
                {selectedSubmission.metadata && (
                  <div>
                    <p className="text-sm text-gray-400 mb-2">Additional Information</p>
                    <div className="p-4 bg-slate-800 rounded-lg">
                      <pre className="text-xs text-gray-300 overflow-x-auto">
                        {JSON.stringify(selectedSubmission.metadata, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
